import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { IFacility } from './api'

export interface IGetLabelFromLocationArgs {
  addressLine1?: string | null
  addressLine2?: string | null
  city?: string | null
  state?: string | null
  postalCode?: string | null
}

/**
 * Generates option label from location values,
 * which may be defined or undefined
 */
export const getLabelFromLocation = ({
  addressLine1 = '',
  addressLine2 = '',
  city = '',
  postalCode = '',
  state = '',
}: IGetLabelFromLocationArgs): string => {
  let addressString = ''
  if (addressLine1) addressString += ` ${addressLine1},`
  if (addressLine2) addressString += ` ${addressLine2},`
  if (city) addressString += ` ${city},`
  if (state) addressString += ` ${state}`
  if (postalCode) addressString += ` ${postalCode}`
  return addressString.trim()
}

/**
 * Generates option label with facility name
 */
export const getFacilityLabelFromLocation = (
  name: string = '',
  location: IGetLabelFromLocationArgs = {}
): string => {
  let addressString = ' · '
  addressString += getLabelFromLocation(location)
  return `${name.trim()}${addressString}`
}

export interface IFacilityLocation {
  location?: ILocationSuggestion
  facility?: ILocationSuggestion
}

const EMPTY_OBJECT = {}

/**
 * Returns an object with id and value, or empty object
 */
export const getLocationValue = (val: IFacilityLocation | undefined): ILocationSuggestion | {} => {
  const { facility, location } = val || {}
  if (location?.city || location?.state) {
    return location
  }

  if (facility?.id) {
    return facility
  }

  return EMPTY_OBJECT
}

/**
 * Returns IAutocomplete option object for mapping results
 */
export const mapFacilityToOption = (
  facility: IFacility
): Pick<IAutocompleteOption, 'id' | 'text'> => {
  const { name = '', addresses = [], id } = facility || {}
  const [address] = addresses || []
  const { addressLine1 = '', locality: city = '', postalCode = '', subdivisionCode: state = '' } =
    address || {}

  const zip = !postalCode || postalCode === null ? undefined : postalCode
  const text = getFacilityLabelFromLocation(name, {
    city,
    state,
    postalCode: zip ?? '',
    addressLine1,
  })

  return {
    text,
    id,
  }
}
